/* eslint camelcase: 0 */
// @ts-check

import last from 'lodash/last';
import {AttrsSchema, Component} from 'panel';

import {commaizeNumber, htmlEncodeString} from 'common/util';
import {Section} from 'common/components/items-menu/types';
import {ColorChoice} from 'common/report/util/funnels/colors';
import {
  ClickBarEvent,
  SelectedBar,
  SelectedSegmentStep,
  TimeBucketDefinition,
  TimeBucketKey,
} from 'common/report/models/funnels/bar-chart/types';
import {
  getBucketAccessor,
  getBucketLabel,
  getFormattedUserPct,
  getMedianAccessor,
  labelFormatter,
} from 'common/report/models/funnels/time-to-convert/utils';
import {formatTime} from 'common/util/date';
import {
  formatSumOfSingleBucketData,
  formatXAxis,
  formatYAxisLabel,
  getBarChartData,
  getBucketData,
  getFunnelStep,
  getLineChartDataforBucketReports,
  getToolTip,
  percentOfTotal,
  truncateBarChartDataForDownload,
} from 'common/report/models/funnels/bar-chart/utils';
import {LineStyle} from 'common/charts/visualization/line-chart/enums';
import {ValueRepresentationType} from 'common/report/constants';
import {
  BasicFunnelStep,
  BucketChartVizType,
  CountType,
  FunnelSegment,
  FunnelStep,
} from 'common/report/models/funnels/types';
import {DEFAULT_CHART_HEIGHT} from 'common/report/constants';
import {createBucketLineChartFunnelData} from 'common/report/models/funnels/utils';
import {ChartDisplayContext} from 'common/widgets/legend-line-chart';
import {ChartLegendTheme} from 'common/components/chart-legend/constants';
import 'common/report/widgets/funnels/bar-chart';

import template from './index.jade';
import css from './index.styl?inline';

interface ChartState {
  chartTooltip: TODO;
  popoverActive: boolean;
  selection: SelectedBar;
  funnelData: Map<string, SelectedSegmentStep>;
  comparisonData: Map<string, SelectedSegmentStep>;
}

interface ChartAttrs {
  'chart-visualization': BucketChartVizType;
  'cohorts-allowed': boolean;
  'cohorts-create-disabled-message': string;
  'cohort-names': string;
  'data-group-id': string;
  'date-range-labels': string[];
  'display-context': ChartDisplayContext;
  'download-mode': boolean;
  'funnels-count-type': CountType;
  'include-header': boolean;
  'is-compact': boolean;
  'legend-sections': Section[];
  'legend-theme': ChartLegendTheme;
  'representative-steps': Array<BasicFunnelStep | FunnelStep>;
  'segment-names': string[];
  'selected-segments': ColorChoice[];
  'should-show-median': boolean;
  'step-index': number;
  'time-buckets': TimeBucketDefinition;
  timezone: string;
  'utc-offset': number;
  'value-representation': ValueRepresentationType;
}

export class FunnelTimeToConvertChart extends Component<ChartState, ChartAttrs> {
  private _getCohortNameAndDescription: any;
  private _segmentData: FunnelSegment[];
  private _comparisonSegmentData: FunnelSegment[];

  /**
   * @property {boolean} cohorts-allowed - true if cohorts can be created from the chart
   * @property {string} cohort-names - names of existing cohorts as json string for popover validation
   * @property {string} data-group-id - the id of the currently selected data group
   * @property {json} segment-data - array of segmented steps for the funnel
   * @property {number} [step-index=null] step-index - index of step being viewed,
   *   default null to represent the entire funnel
   * @property {json} time-buckets - request params for the time-to-convert data
   * @property {number} time-buckets.bucket_size - size of buckets in seconds, default 0 to indicate no bucketing
   * @property {number=} time-buckets.offset - offset from the start in seconds, default 0 for no offset
   */
  static get attrsSchema(): AttrsSchema<ChartAttrs> {
    return {
      ...super.attrsSchema,
      'chart-visualization': {type: `string`, default: BucketChartVizType.Bar},
      'cohorts-allowed': `boolean`,
      'cohorts-create-disabled-message': {type: `string`, default: ``},
      'cohort-names': {type: `string`},
      'data-group-id': {type: `string`, default: null},
      'date-range-labels': {type: `json`, default: []},
      'display-context': {
        type: `string`,
        enum: Object.values(ChartDisplayContext),
        default: ChartDisplayContext.Primary,
      },
      'download-mode': {type: `boolean`},
      'funnels-count-type': {type: `string`, default: null},
      'include-header': `boolean`,
      'is-compact': `boolean`,
      'legend-sections': {type: `json`},
      'legend-theme': {type: `string`, enum: Object.values(ChartLegendTheme), default: ChartLegendTheme.Primary},
      'representative-steps': {type: `json`, default: []},
      'segment-names': {type: `json`, default: []},
      'selected-segments': {type: `json`, default: []},
      'should-show-median': `boolean`,
      'step-index': {type: `number`, default: null},
      'time-buckets': {type: `json`, default: {}},
      timezone: `string`,
      'utc-offset': `number`,
      'value-representation': `string`,
    };
  }

  connectedCallback() {
    super.connectedCallback();
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        chartTooltip: null,
        popoverActive: false,
        selection: null,
        funnelData: null,
        comparisonData: null,
      },
      helpers: {
        ValueRepresentationType,
        // always hidden for first step
        isHidden: () => this.stepIndex === 0,
        hasData: () => {
          const stepData = this.segmentData?.[0]?.steps;
          if (!stepData || stepData.length === 0) {
            return false;
          }

          if (this.stepIndex === 0) {
            return false;
          }

          const lastStep: FunnelStep = last(stepData);
          return this.stepIndex ? TimeBucketKey.PREV in stepData[this.stepIndex] : TimeBucketKey.START in lastStep;
        },
        isDrilledDown: () => this.isDrilledDown,
        subtitleText: () => {
          const funnelStep = getFunnelStep(this.attr(`representative-steps`), this.stepIndex);
          const bucketData = getBucketData(funnelStep, getBucketAccessor(this.stepIndex));
          const conversions = `${formatSumOfSingleBucketData(bucketData)} conversions`;
          return {
            conversionText: this.isDrilledDown ? `${conversions} in ${this.timeSelection}` : conversions,
            totalConversionPercentageText: `${commaizeNumber(funnelStep.count)} total (${getFormattedUserPct(
              funnelStep,
              getBucketAccessor(this.stepIndex),
            )}%)`,
          };
        },
        showBarChart: () => {
          return this.attr(`chart-visualization`) === BucketChartVizType.Bar;
        },
        passThrough: (ev) => {
          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(ev.type, ev));
        },

        /** Bar Chart Methods */
        chartLegendAttrs: () => {
          let height = `calc(100vh - 123px)`;
          if (!this.helpers.showBarChart()) {
            height = `${DEFAULT_CHART_HEIGHT}px`;
          }

          return {
            height,
            sections: JSON.stringify(this.attr(`legend-sections`)),
            'enable-search': true,
            'show-select-all': true,
            theme: this.attr(`download-mode`) ? ChartLegendTheme.ReadOnly : ChartLegendTheme.Primary,
          };
        },
        getChartToolTipAttrs: (sections, color) => {
          return {
            sections: JSON.stringify(sections),
            ...(!this.hasComparison() && {'title-color': color}),
          };
        },
        barChartData: () => {
          const overallStep = this.attr(`representative-steps`)
            ? getFunnelStep(this.attr(`representative-steps`), this.stepIndex)
            : null;
          const segmentData = [];

          this.funnelData.forEach((value, key) => {
            if (this.comparisonData) {
              segmentData.push(this.comparisonData.get(key));
            }
            segmentData.push(value);
          });

          const data = getBarChartData({
            segmentData,
            overallStep,
            selectedBucket: this.attr(`time-buckets`),
            selection: this.state.selection,
            bucketAccessor: getBucketAccessor(this.stepIndex),
            medianAccessor: getMedianAccessor(this.stepIndex),
            labelFormatter,
            showAnnotation: this.attr(`should-show-median`),
          });

          if (this.attr(`download-mode`)) {
            return truncateBarChartDataForDownload(data);
          } else {
            return {data, isTruncated: false};
          }
        },
        hoverBar: ({detail}) => {
          const {isAnnotation, bucket, segmentIndex, x, y} = detail;

          const {color, label, name} = bucket.segments[segmentIndex];
          const left = x - this.getBoundingClientRect().left + 20;
          const top = y - this.getBoundingClientRect().top + 10;

          this.update({
            chartTooltip: {
              color,
              label,
              sections: getToolTip(
                bucket.label,
                name,
                bucket.queryIndex,
                isAnnotation,
                labelFormatter,
                this.funnelData,
                this.comparisonData,
                getBucketAccessor(this.stepIndex),
                getMedianAccessor(this.stepIndex),
                this.attr(`date-range-labels`),
              ),
              left,
              top,
            },
          });
        },
        offHoverBar: () => this.update({chartTooltip: null}),
        clickBar: (ev: ClickBarEvent) => {
          const {left, top} = this.getBoundingClientRect();
          const {clientX, clientY} = ev.detail.original;
          const selection = {...ev.detail, clientX: clientX - left, clientY: clientY - top};
          this.update({
            chartTooltip: null,
            popoverActive: true,
          });
          this.dispatchEvent(
            new CustomEvent(`openPopover`, {
              detail: {
                selection,
              },
            }),
          );
        },

        /** Line Chart Methods */
        isSegmented: () => this.segmentData?.length > 1,
        lineChartData: () => {
          const bucketAccessor = getBucketAccessor(this.stepIndex);
          let chartData = getLineChartDataforBucketReports({
            segmentData: this.segmentData,
            stepIndex: this.stepIndex,
            bucketAccessor: bucketAccessor as keyof FunnelStep,
            segmentColors: this.attr(`selected-segments`),
            isComparison: false,
            representationType: this.attr(`value-representation`),
          });
          if (this.hasComparison()) {
            chartData = [
              ...chartData,
              ...getLineChartDataforBucketReports({
                segmentData: this._comparisonSegmentData,
                stepIndex: this.stepIndex,
                bucketAccessor: bucketAccessor as keyof FunnelStep,
                segmentColors: this.attr(`selected-segments`),
                isComparison: true,
                representationType: this.attr(`value-representation`),
              }),
            ];
          }
          return chartData;
        },
        lineChartClick: (series: any, index: number, color: string, clientX: number, clientY: number) => {
          if (series.options.dashStyle === LineStyle.Comparison) {
            return;
          }
          const {name: segmentKey} = series;
          const {step: funnelStep, segmentName} = this.funnelData.get(segmentKey);
          const bucketData = getBucketData(funnelStep, getBucketAccessor(this.stepIndex));
          const buckets = bucketData.buckets;
          const count = buckets[index];
          const selection = {
            segment: {
              color,
              count,
              percentage: percentOfTotal(bucketData, count),
              isSelected: true,
              isClickable: true,
              label: segmentName,
              name: segmentKey,
            },
            rowLabel: formatXAxis(index, this.attr(`time-buckets`), getBucketLabel),
            queryIndex: index,
            clientX,
            clientY,
          };

          this.dispatchEvent(
            new CustomEvent(`openPopover`, {
              detail: {
                selection,
              },
            }),
          );
          this.update({
            chartTooltip: null,
            popoverActive: true,
          });
        },
        formatTooltip: (x: number, _y: number, series: any, _idx: number) => {
          const {color, name: seriesName} = series;
          let titleColor = ``;

          if (!this.hasComparison()) {
            titleColor = `title-color=${color}`;
          }
          const isCompact = this.attr(`is-compact`) ? `is-compact` : ``;
          const toolTipAttrs = `${titleColor} ${isCompact} sections="${htmlEncodeString(
            JSON.stringify(
              getToolTip(
                formatXAxis(x, this.attr(`time-buckets`), getBucketLabel, true),
                seriesName,
                x,
                false,
                labelFormatter,
                this.state.funnelData,
                this.state.comparisonData,
                getBucketAccessor(this.stepIndex) as keyof FunnelStep,
                getMedianAccessor(this.stepIndex) as keyof FunnelStep,
                this.attr(`date-range-labels`),
              ),
            ),
          )}"`;

          return `
                <mp-chart-tooltip ${toolTipAttrs}>
              `;
        },
        formatXAxisLabel: (value: number) => {
          return formatXAxis(value, this.attr(`time-buckets`), getBucketLabel);
        },
        formatYAxisLabel: (value: number) => {
          return formatYAxisLabel(value, this.attr(`value-representation`));
        },
      },
    };
  }

  get isDrilledDown() {
    return this.attr(`time-buckets`) && this.attr(`time-buckets`).isDrilledDown;
  }

  /** Currently selected conversion time window, formatted */
  get timeSelection() {
    const timeBuckets = this.attr(`time-buckets`);
    if (!timeBuckets.max) {
      return null;
    }

    return formatTime(timeBuckets.min, timeBuckets.max);
  }

  get isViewingSingleStep() {
    const stepIndex = this.stepIndex;
    return stepIndex && stepIndex > 0;
  }

  hasComparison() {
    return this.comparisonSegmentData?.length > 0;
  }

  createData(segmentData: FunnelSegment[], isComparisonData = false): Map<string, SelectedSegmentStep> {
    return createBucketLineChartFunnelData({
      segmentData,
      selected: this.attr(`selected-segments`),
      stepIndex: this.stepIndex,
      hasComparison: this.hasComparison(),
      isComparisonData,
    });
  }

  get funnelData(): Map<string, SelectedSegmentStep> {
    return this.state.funnelData;
  }

  get comparisonData(): Map<string, SelectedSegmentStep> {
    return this.state.comparisonData;
  }

  set segmentData(val: FunnelSegment[]) {
    this._segmentData = val;
    this.update({funnelData: this.createData(val)});
  }

  get segmentData(): FunnelSegment[] {
    return this._segmentData;
  }

  set comparisonSegmentData(val: FunnelSegment[]) {
    this._comparisonSegmentData = val;
    this.update({comparisonData: this.createData(val, true)});
  }

  get comparisonSegmentData(): FunnelSegment[] {
    return this._comparisonSegmentData;
  }

  set getCohortNameAndDescription(fn) {
    this._getCohortNameAndDescription = fn;
  }

  get stepIndex() {
    return this.attr(`step-index`);
  }

  getCohortDetails({conversionRange, segment}) {
    return this._getCohortNameAndDescription({conversionRange, segment});
  }
}

customElements.define(`mp-funnel-time-to-convert-chart`, FunnelTimeToConvertChart);
